if (!customElements.get('findsy-sticky-cart')) {
  customElements.define(
    'findsy-sticky-cart',
    class FindsyStickyCart extends HTMLElement {
      constructor() {
        super();
        this.sectionId = this.dataset.sectionId;
        this.observer = undefined;
        this.variantChangeUnsubscriber = undefined;
      }

      connectedCallback() {
        const targetId = this.dataset.watchTarget;
        const target = targetId ? document.getElementById(targetId) : null;

        // Nothing to be "sticky" for (e.g. the buy_buttons block was
        // removed) - stay hidden rather than show a non-functional bar.
        if (!target) return;

        this.observer = new IntersectionObserver(
          (entries) => {
            const entry = entries[0];
            // Only show once the canonical CTA has scrolled UP past the
            // top of the viewport. Not intersecting also happens before
            // the user ever reaches it (still below the viewport) - that
            // case must stay hidden, hence the boundingClientRect check
            // rather than isIntersecting alone.
            const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            this.toggleAttribute('hidden', !scrolledPast);
          },
          { threshold: 0 }
        );
        this.observer.observe(target);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            this.handleVariantChange(event);
          });
        }
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.variantChangeUnsubscriber?.();
      }

      handleVariantChange(event) {
        if (!event?.data || String(event.data.sectionId) !== String(this.sectionId)) return;
        const { html } = event.data;
        if (!html) return;

        const priceDestination = this.querySelector(`#FindsyStickyPrice-${this.sectionId}`);
        const priceSource = html.getElementById(`price-${this.sectionId}`);
        if (priceDestination && priceSource) {
          priceDestination.innerHTML = priceSource.innerHTML;
        }

        const submitDestination = this.querySelector(`#FindsyStickySubmit-${this.sectionId}`);
        const submitSource = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
        if (submitDestination && submitSource) {
          submitDestination.toggleAttribute('disabled', submitSource.hasAttribute('disabled'));
          const destinationLabel = submitDestination.querySelector('span');
          const sourceLabel = submitSource.querySelector('span');
          if (destinationLabel && sourceLabel) {
            destinationLabel.textContent = sourceLabel.textContent;
          }
        }
      }
    }
  );
}

// Dawn's ProductForm.toggleSubmitButton() always resets the enabled-state
// label to the generic window.variantStrings.addToCart, which drops
// Findsy's "Add to cart — $69.99" price suffix (show_price_in_button in
// buy-buttons.liquid) on every variant switch. Rather than fight that
// (Dawn's disabled/enabled + sold-out text handling is otherwise correct
// and must stay canonical), re-sync the label from the same fetched HTML
// fragment product-info.js already has on hand for this variant - exactly
// the technique above for the sticky button, just for the primary CTA.
// The fragment's button span already carries the right text for every
// state (price-suffixed when available, Dawn's sold_out/unavailable
// strings with no suffix otherwise), so this never invents new copy.
if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
  subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
    const { sectionId, html } = event?.data || {};
    if (!sectionId || !html) return;

    const destination = document.getElementById(`ProductSubmitButton-${sectionId}`);
    const source = html.getElementById(`ProductSubmitButton-${sectionId}`);
    if (!destination || !source) return;

    const destinationLabel = destination.querySelector('span');
    const sourceLabel = source.querySelector('span');
    if (destinationLabel && sourceLabel) {
      destinationLabel.textContent = sourceLabel.textContent;
    }
  });
}

if (!customElements.get('findsy-demo-grid')) {
  customElements.define(
    'findsy-demo-grid',
    // Native <video controls> is intentionally off (see
    // sections/findsy-product-demo.liquid) - fullscreen on a portrait UGC
    // clip crops it badly, plus it drags in download/PiP UI nobody asked
    // for. This drives a custom Play/Pause button pair instead: Play (or a
    // tap on the video) creates the real <video> from its inert <template>
    // on first use, keeping the initial page load video-free; Pause and the
    // video's own play/pause/ended events keep the two buttons and the
    // video in sync from then on.
    class FindsyDemoGrid extends HTMLElement {
      connectedCallback() {
        this.addEventListener('click', this.handleClick.bind(this));
      }

      handleClick(event) {
        const playButton = event.target.closest('.findsy-demo__play');
        if (playButton && this.contains(playButton)) {
          this.playPlayer(playButton.closest('.findsy-demo__player'));
          return;
        }

        const pauseButton = event.target.closest('.findsy-demo__pause');
        if (pauseButton && this.contains(pauseButton)) {
          this.pausePlayer(pauseButton.closest('.findsy-demo__player'));
          return;
        }

        // Clicking the video itself toggles play/pause too.
        const video = event.target.closest('.findsy-demo__video');
        if (video && this.contains(video)) {
          const player = video.closest('.findsy-demo__player');
          if (video.paused) this.playPlayer(player);
          else this.pausePlayer(player);
        }
      }

      playPlayer(player) {
        const video = this.ensureVideo(player);
        if (!video) return;

        // Only one PDP demo video plays at a time.
        this.querySelectorAll('.findsy-demo__video').forEach((other) => {
          if (other !== video && !other.paused) other.pause();
        });

        // Re-assert on every play, not just at creation - cheap, and means
        // this video can never start with sound no matter what set .muted.
        video.muted = true;

        video.play?.().catch(() => {
          // Playback was blocked - Play stays visible so the visitor can
          // just try again; nothing else to do.
        });
      }

      pausePlayer(player) {
        player?.querySelector('.findsy-demo__video')?.pause();
      }

      /** Creates the real <video> from its <template> on first play, then reuses it. */
      ensureVideo(player) {
        if (!player) return null;
        const existing = player.querySelector('.findsy-demo__video');
        if (existing) return existing;

        const template = player.querySelector('template');
        const fragment = template?.content.cloneNode(true);
        const video = fragment?.querySelector('video');
        if (!template || !video) return null;

        // Belt-and-braces beyond the `muted` HTML attribute already on this
        // <template>'s <video> (see sections/findsy-product-demo.liquid):
        // set the live properties directly so this can never start with
        // sound, regardless of how reliably a given browser reflects
        // defaultMuted from a cloned, template-sourced element.
        video.muted = true;
        video.defaultMuted = true;

        template.replaceWith(fragment);

        video.addEventListener('play', () => this.setPlayingState(player, true));
        video.addEventListener('pause', () => this.setPlayingState(player, false));
        video.addEventListener('ended', () => this.setPlayingState(player, false));
        return video;
      }

      setPlayingState(player, isPlaying) {
        player.classList.toggle('is-playing', isPlaying);
        player.querySelector('.findsy-demo__play')?.toggleAttribute('hidden', isPlaying);
        player.querySelector('.findsy-demo__pause')?.toggleAttribute('hidden', !isPlaying);
      }
    }
  );
}
